import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  ScreenOnOffReportData,
  ScreenOffHistoryData,
  ScreenOffHistoryFilters,
  ScreenTimelineData,
  ScreenTimelineFilters,
  CreativeReportData,
  CreativeReportSummary,
  CreativeScreenPlayInfo,
  CreativeReportScreensFilters,
  ActiveScreenReportData,
  ActiveScreenInfo,
  ActiveScreensListFilters,
  OffScreenInfo,
  OffScreensListFilters,
  ScreenWithoutScheduleInfo,
  ScreensWithoutSchedulesListFilters,
  ReportListPage,
  LogCreativePlayInput,
  BatchLogCreativePlaysInput,
  ReportFormat,
  OffScreenColumnFilter,
  NoScheduleColumnFilter,
  OffHistoryColumnFilter,
} from "../../schemas/reportSchema";

// Summary + charts only. The Off Screen Details and no-schedule rows come from
// their own paginated queries below, so this payload stays a fixed size no
// matter how many screens the tenant has. The charts always describe the whole
// tenant — only the tables below take filters.
const GET_SCREEN_ON_OFF_REPORT = gql`
  query ScreenOnOffReport {
    screenOnOffReport {
      totalScreens
      totalOnScreens
      totalOffScreens
      totalScreensWithoutSchedules
      screensOffThanPreviousDay
      screensOffForMoreThanWeek
      previous7DaysGraph {
        date
        offScreens
      }
    }
  }
`;

const GET_OFF_SCREENS_LIST = gql`
  query OffScreensList(
    $columnFilters: [OffScreenColumnFilter!]
    $limit: Int
    $offset: Int
    $minOffDays: Int
  ) {
    offScreensList(
      columnFilters: $columnFilters
      limit: $limit
      offset: $offset
      minOffDays: $minOffDays
    ) {
      items {
        screenName
        region
        city
        orientation
        offForDays
      }
      totalCount
    }
  }
`;

const GET_SCREENS_WITHOUT_SCHEDULES_LIST = gql`
  query ScreensWithoutSchedulesList(
    $columnFilters: [NoScheduleColumnFilter!]
    $limit: Int
    $offset: Int
  ) {
    screensWithoutSchedulesList(
      columnFilters: $columnFilters
      limit: $limit
      offset: $offset
    ) {
      items {
        screenName
        region
        city
        orientation
      }
      totalCount
    }
  }
`;

const GET_SCREEN_OFF_HISTORY = gql`
  query ScreenOffHistory(
    $startDate: String
    $endDate: String
    $columnFilters: [OffHistoryColumnFilter!]
    $limit: Int
    $offset: Int
  ) {
    screenOffHistory(
      startDate: $startDate
      endDate: $endDate
      columnFilters: $columnFilters
      limit: $limit
      offset: $offset
    ) {
      entries {
        id
        screenId
        screenName
        region
        city
        orientation
        offTimeStart
        offTimeEnd
        totalOffTime
        remarks
      }
      totalCount
      ongoingCount
      totalOffMinutes
    }
  }
`;

const GET_SCREEN_STATUS_TIMELINE = gql`
  query ScreenStatusTimeline(
    $screenId: ID!
    $startDate: String
    $endDate: String
  ) {
    screenStatusTimeline(
      screenId: $screenId
      startDate: $startDate
      endDate: $endDate
    ) {
      screenId
      screenName
      rangeStart
      rangeEnd
      currentStatus
      segments {
        status
        startTime
        endTime
        durationMinutes
        isOngoing
      }
      totalOnMinutes
      totalOffMinutes
      offCount
      uptimePercentage
    }
  }
`;

// Header figures only — the played-screens rows are paginated separately.
const GET_CREATIVE_REPORT_SUMMARY = gql`
  query CreativeReportSummary(
    $creativeId: ID!
    $startDate: String
    $endDate: String
    $cityId: ID
    $regionId: ID
  ) {
    creativeReport(
      creativeId: $creativeId
      startDate: $startDate
      endDate: $endDate
      cityId: $cityId
      regionId: $regionId
    ) {
      creativeName
      startDate
      endDate
      fileUrl
      fileType
      totalScreensPlayed
      totalScreenTime
      numberOfTimesPlayed
      averageRepetitionFrequency
    }
  }
`;

// Full report including every played screen. Only used by the editable
// download flow, which needs all rows in memory to edit and total them.
const GET_CREATIVE_REPORT = gql`
  query CreativeReport(
    $creativeId: ID!
    $startDate: String
    $endDate: String
    $cityId: ID
    $regionId: ID
  ) {
    creativeReport(
      creativeId: $creativeId
      startDate: $startDate
      endDate: $endDate
      cityId: $cityId
      regionId: $regionId
    ) {
      creativeName
      startDate
      endDate
      fileUrl
      fileType
      totalScreensPlayed
      totalScreenTime
      numberOfTimesPlayed
      averageRepetitionFrequency
      screensPlayed {
        screenName
        restaurantName
        city
        region
        duration
        numberOfTimesPlayed
      }
    }
  }
`;

const GET_CREATIVE_REPORT_SCREENS = gql`
  query CreativeReportScreens(
    $creativeId: ID!
    $startDate: String
    $endDate: String
    $cityId: ID
    $regionId: ID
    $search: String
    $limit: Int
    $offset: Int
  ) {
    creativeReportScreens(
      creativeId: $creativeId
      startDate: $startDate
      endDate: $endDate
      cityId: $cityId
      regionId: $regionId
      search: $search
      limit: $limit
      offset: $offset
    ) {
      items {
        screenName
        restaurantName
        city
        region
        duration
        numberOfTimesPlayed
      }
      totalCount
    }
  }
`;

const GET_ACTIVE_SCREEN_REPORT = gql`
  query ActiveScreenReport($date: String) {
    activeScreenReport(date: $date) {
      activeScreens {
        screenName
        restaurantName
        city
        region
        orientation
      }
    }
  }
`;

const GET_ACTIVE_SCREENS_LIST = gql`
  query ActiveScreensList(
    $date: String
    $search: String
    $limit: Int
    $offset: Int
  ) {
    activeScreensList(
      date: $date
      search: $search
      limit: $limit
      offset: $offset
    ) {
      items {
        screenName
        restaurantName
        city
        region
        orientation
      }
      totalCount
    }
  }
`;

const LOG_CREATIVE_PLAY = gql`
  mutation LogCreativePlay($input: LogCreativePlayInput!) {
    logCreativePlay(input: $input)
  }
`;

const BATCH_LOG_CREATIVE_PLAYS = gql`
  mutation BatchLogCreativePlays($input: BatchLogCreativePlaysInput!) {
    batchLogCreativePlays(input: $input)
  }
`;

const DOWNLOAD_SCREEN_ON_OFF_REPORT = gql`
  query DownloadScreenOnOffReport(
    $offScreenColumnFilters: [OffScreenColumnFilter!]
    $noScheduleColumnFilters: [NoScheduleColumnFilter!]
    $minOffDays: Int
    $format: ReportFormat
  ) {
    downloadScreenOnOffReport(
      offScreenColumnFilters: $offScreenColumnFilters
      noScheduleColumnFilters: $noScheduleColumnFilters
      minOffDays: $minOffDays
      format: $format
    ) {
      base64
      fileName
      mimeType
    }
  }
`;

const DOWNLOAD_SCREENS_WITHOUT_SCHEDULES = gql`
  query DownloadScreensWithoutSchedules(
    $columnFilters: [NoScheduleColumnFilter!]
    $format: ReportFormat
  ) {
    downloadScreensWithoutSchedules(
      columnFilters: $columnFilters
      format: $format
    ) {
      base64
      fileName
      mimeType
    }
  }
`;

const DOWNLOAD_ACTIVE_SCREEN_REPORT = gql`
  query DownloadActiveScreenReport($date: String, $format: ReportFormat) {
    downloadActiveScreenReport(date: $date, format: $format) {
      base64
      fileName
      mimeType
    }
  }
`;

const DOWNLOAD_SCREEN_OFF_HISTORY = gql`
  query DownloadScreenOffHistory(
    $startDate: String
    $endDate: String
    $columnFilters: [OffHistoryColumnFilter!]
    $format: ReportFormat
  ) {
    downloadScreenOffHistory(
      startDate: $startDate
      endDate: $endDate
      columnFilters: $columnFilters
      format: $format
    ) {
      base64
      fileName
      mimeType
    }
  }
`;

const DOWNLOAD_SCREEN_STATUS_TIMELINE = gql`
  query DownloadScreenStatusTimeline(
    $screenId: ID!
    $startDate: String
    $endDate: String
    $format: ReportFormat
  ) {
    downloadScreenStatusTimeline(
      screenId: $screenId
      startDate: $startDate
      endDate: $endDate
      format: $format
    ) {
      base64
      fileName
      mimeType
    }
  }
`;

const DOWNLOAD_CREATIVE_REPORT = gql`
  query DownloadCreativeReport(
    $creativeId: ID!
    $startDate: String
    $endDate: String
    $cityId: ID
    $regionId: ID
    $format: ReportFormat
  ) {
    downloadCreativeReport(
      creativeId: $creativeId
      startDate: $startDate
      endDate: $endDate
      cityId: $cityId
      regionId: $regionId
      format: $format
    ) {
      base64
      fileName
      mimeType
    }
  }
`;

const SEND_SCREEN_ON_OFF_REPORT_EMAIL = gql`
  mutation SendScreenOnOffReportEmail(
    $input: SendScreenOnOffReportEmailInput!
  ) {
    sendScreenOnOffReportEmail(input: $input)
  }
`;

const SEND_SCREENS_WITHOUT_SCHEDULES_EMAIL = gql`
  mutation SendScreensWithoutSchedulesReportEmail(
    $input: SendScreensWithoutSchedulesEmailInput!
  ) {
    sendScreensWithoutSchedulesReportEmail(input: $input)
  }
`;

const SEND_ACTIVE_SCREEN_REPORT_EMAIL = gql`
  mutation SendActiveScreenReportEmail(
    $input: SendActiveScreenReportEmailInput!
  ) {
    sendActiveScreenReportEmail(input: $input)
  }
`;

const SEND_SCREEN_OFF_HISTORY_REPORT_EMAIL = gql`
  mutation SendScreenOffHistoryReportEmail(
    $input: SendScreenOffHistoryReportEmailInput!
  ) {
    sendScreenOffHistoryReportEmail(input: $input)
  }
`;

const SEND_CREATIVE_REPORT_EMAIL = gql`
  mutation SendCreativeReportEmail($input: SendCreativeReportEmailInput!) {
    sendCreativeReportEmail(input: $input)
  }
`;

const DOWNLOAD_CREATIVE_REPORT_WITH_EDITS = gql`
  mutation DownloadCreativeReportWithEdits(
    $input: DownloadCreativeReportInput!
  ) {
    downloadCreativeReport(input: $input) {
      base64
      fileName
      mimeType
    }
  }
`;

export const reportService = {
  getScreenOnOffReport: async (): Promise<ScreenOnOffReportData> => {
    const { data } = await client.query({
      query: GET_SCREEN_ON_OFF_REPORT,
      fetchPolicy: "network-only",
    });
    return data.screenOnOffReport;
  },

  getOffScreensList: async (
    filters?: OffScreensListFilters,
  ): Promise<ReportListPage<OffScreenInfo>> => {
    const { data } = await client.query({
      query: GET_OFF_SCREENS_LIST,
      variables: {
        columnFilters: filters?.columnFilters?.length
          ? filters.columnFilters
          : null,
        limit: filters?.limit ?? null,
        offset: filters?.offset ?? null,
        // `?? null` rather than `|| null`, so an explicit 0 ("show every
        // currently-off screen") is sent instead of falling back to the default.
        minOffDays: filters?.minOffDays ?? null,
      },
      fetchPolicy: "network-only",
    });
    return data.offScreensList;
  },

  getScreensWithoutSchedulesList: async (
    filters?: ScreensWithoutSchedulesListFilters,
  ): Promise<ReportListPage<ScreenWithoutScheduleInfo>> => {
    const { data } = await client.query({
      query: GET_SCREENS_WITHOUT_SCHEDULES_LIST,
      variables: {
        columnFilters: filters?.columnFilters?.length
          ? filters.columnFilters
          : null,
        limit: filters?.limit ?? null,
        offset: filters?.offset ?? null,
      },
      fetchPolicy: "network-only",
    });
    return data.screensWithoutSchedulesList;
  },

  getScreenOffHistory: async (
    filters?: ScreenOffHistoryFilters,
  ): Promise<ScreenOffHistoryData> => {
    const { data } = await client.query({
      query: GET_SCREEN_OFF_HISTORY,
      variables: {
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        columnFilters: filters?.columnFilters?.length
          ? filters.columnFilters
          : null,
        limit: filters?.limit ?? null,
        offset: filters?.offset ?? null,
      },
      fetchPolicy: "network-only",
    });
    return data.screenOffHistory;
  },

  getScreenStatusTimeline: async (
    screenId: string,
    filters?: ScreenTimelineFilters,
  ): Promise<ScreenTimelineData> => {
    const { data } = await client.query({
      query: GET_SCREEN_STATUS_TIMELINE,
      variables: {
        screenId,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
      },
      fetchPolicy: "network-only",
    });
    return data.screenStatusTimeline;
  },

  downloadScreenStatusTimeline: async (
    screenId: string,
    filters?: ScreenTimelineFilters & { format?: ReportFormat | null },
  ): Promise<{ base64: string; fileName: string; mimeType: string }> => {
    const { data } = await client.query({
      query: DOWNLOAD_SCREEN_STATUS_TIMELINE,
      variables: {
        screenId,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        format: filters?.format || null,
      },
      fetchPolicy: "network-only",
    });
    return data.downloadScreenStatusTimeline;
  },

  downloadScreenOffHistory: async (filters?: {
    startDate?: string | null;
    endDate?: string | null;
    columnFilters?: OffHistoryColumnFilter[] | null;
    format?: ReportFormat | null;
  }): Promise<{ base64: string; fileName: string; mimeType: string }> => {
    const { data } = await client.query({
      query: DOWNLOAD_SCREEN_OFF_HISTORY,
      variables: {
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        columnFilters: filters?.columnFilters?.length
          ? filters.columnFilters
          : null,
        format: filters?.format || null,
      },
      fetchPolicy: "network-only",
    });
    return data.downloadScreenOffHistory;
  },

  sendScreenOffHistoryReportEmail: async (
    email: string,
    filters?: {
      startDate?: string | null;
      endDate?: string | null;
      columnFilters?: OffHistoryColumnFilter[] | null;
      format?: ReportFormat | null;
    },
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: SEND_SCREEN_OFF_HISTORY_REPORT_EMAIL,
      variables: {
        input: {
          email,
          startDate: filters?.startDate || null,
          endDate: filters?.endDate || null,
          columnFilters: filters?.columnFilters?.length
            ? filters.columnFilters
            : null,
          format: filters?.format || null,
        },
      },
    });
    return data.sendScreenOffHistoryReportEmail;
  },

  getCreativeReportSummary: async (
    creativeId: string,
    filters?: {
      startDate?: string | null;
      endDate?: string | null;
      cityId?: string | null;
      regionId?: string | null;
    },
  ): Promise<CreativeReportSummary> => {
    const { data } = await client.query({
      query: GET_CREATIVE_REPORT_SUMMARY,
      variables: {
        creativeId,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        cityId: filters?.cityId || null,
        regionId: filters?.regionId || null,
      },
      fetchPolicy: "network-only",
    });
    return data.creativeReport;
  },

  getCreativeReport: async (
    creativeId: string,
    filters?: {
      startDate?: string | null;
      endDate?: string | null;
      cityId?: string | null;
      regionId?: string | null;
    },
  ): Promise<CreativeReportData> => {
    const { data } = await client.query({
      query: GET_CREATIVE_REPORT,
      variables: {
        creativeId,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        cityId: filters?.cityId || null,
        regionId: filters?.regionId || null,
      },
      fetchPolicy: "network-only",
    });
    return data.creativeReport;
  },

  getCreativeReportScreens: async (
    creativeId: string,
    filters?: CreativeReportScreensFilters,
  ): Promise<ReportListPage<CreativeScreenPlayInfo>> => {
    const { data } = await client.query({
      query: GET_CREATIVE_REPORT_SCREENS,
      variables: {
        creativeId,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        cityId: filters?.cityId || null,
        regionId: filters?.regionId || null,
        search: filters?.search || null,
        limit: filters?.limit ?? null,
        offset: filters?.offset ?? null,
      },
      fetchPolicy: "network-only",
    });
    return data.creativeReportScreens;
  },

  getActiveScreenReport: async (
    date?: string | null,
  ): Promise<ActiveScreenReportData> => {
    const { data } = await client.query({
      query: GET_ACTIVE_SCREEN_REPORT,
      variables: {
        date: date || null,
      },
      fetchPolicy: "network-only",
    });
    return data.activeScreenReport;
  },

  getActiveScreensList: async (
    filters?: ActiveScreensListFilters,
  ): Promise<ReportListPage<ActiveScreenInfo>> => {
    const { data } = await client.query({
      query: GET_ACTIVE_SCREENS_LIST,
      variables: {
        date: filters?.date || null,
        search: filters?.search || null,
        limit: filters?.limit ?? null,
        offset: filters?.offset ?? null,
      },
      fetchPolicy: "network-only",
    });
    return data.activeScreensList;
  },

  logCreativePlay: async (input: LogCreativePlayInput): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: LOG_CREATIVE_PLAY,
      variables: { input },
    });
    return data.logCreativePlay;
  },

  batchLogCreativePlays: async (
    input: BatchLogCreativePlaysInput,
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: BATCH_LOG_CREATIVE_PLAYS,
      variables: { input },
    });
    return data.batchLogCreativePlays;
  },

  // The exported file carries both On/Off tables, so each tab's column filters
  // are sent separately and the file matches what is on screen.
  downloadScreenOnOffReport: async (filters?: {
    offScreenColumnFilters?: OffScreenColumnFilter[] | null;
    noScheduleColumnFilters?: NoScheduleColumnFilter[] | null;
    minOffDays?: number | null;
    format?: ReportFormat | null;
  }): Promise<{ base64: string; fileName: string; mimeType: string }> => {
    const { data } = await client.query({
      query: DOWNLOAD_SCREEN_ON_OFF_REPORT,
      variables: {
        offScreenColumnFilters: filters?.offScreenColumnFilters?.length
          ? filters.offScreenColumnFilters
          : null,
        noScheduleColumnFilters: filters?.noScheduleColumnFilters?.length
          ? filters.noScheduleColumnFilters
          : null,
        // `?? null` so an explicit 0 survives, matching the list query.
        minOffDays: filters?.minOffDays ?? null,
        format: filters?.format || null,
      },
      fetchPolicy: "network-only",
    });
    return data.downloadScreenOnOffReport;
  },

  // The on/off report file is the off-screens table only, so this tab needs
  // its own export rather than a section inside that one.
  downloadScreensWithoutSchedules: async (filters?: {
    columnFilters?: NoScheduleColumnFilter[] | null;
    format?: ReportFormat | null;
  }): Promise<{ base64: string; fileName: string; mimeType: string }> => {
    const { data } = await client.query({
      query: DOWNLOAD_SCREENS_WITHOUT_SCHEDULES,
      variables: {
        columnFilters: filters?.columnFilters?.length
          ? filters.columnFilters
          : null,
        format: filters?.format || null,
      },
      fetchPolicy: "network-only",
    });
    return data.downloadScreensWithoutSchedules;
  },

  sendScreensWithoutSchedulesReportEmail: async (
    email: string,
    filters?: {
      columnFilters?: NoScheduleColumnFilter[] | null;
      format?: ReportFormat | null;
    },
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: SEND_SCREENS_WITHOUT_SCHEDULES_EMAIL,
      variables: {
        input: {
          email,
          columnFilters: filters?.columnFilters?.length
            ? filters.columnFilters
            : null,
          format: filters?.format || null,
        },
      },
    });
    return data.sendScreensWithoutSchedulesReportEmail;
  },

  downloadActiveScreenReport: async (
    date?: string | null,
    format?: ReportFormat | null,
  ): Promise<{
    base64: string;
    fileName: string;
    mimeType: string;
  }> => {
    const { data } = await client.query({
      query: DOWNLOAD_ACTIVE_SCREEN_REPORT,
      variables: {
        date: date || null,
        format: format || null,
      },
      fetchPolicy: "network-only",
    });
    return data.downloadActiveScreenReport;
  },

  downloadCreativeReport: async (
    creativeId: string,
    filters?: {
      startDate?: string | null;
      endDate?: string | null;
      cityId?: string | null;
      regionId?: string | null;
      format?: ReportFormat | null;
    },
  ): Promise<{ base64: string; fileName: string; mimeType: string }> => {
    const { data } = await client.query({
      query: DOWNLOAD_CREATIVE_REPORT,
      variables: {
        creativeId,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        cityId: filters?.cityId || null,
        regionId: filters?.regionId || null,
        format: filters?.format || null,
      },
      fetchPolicy: "network-only",
    });
    return data.downloadCreativeReport;
  },

  sendScreenOnOffReportEmail: async (
    email: string,
    filters?: {
      offScreenColumnFilters?: OffScreenColumnFilter[] | null;
      noScheduleColumnFilters?: NoScheduleColumnFilter[] | null;
      minOffDays?: number | null;
      format?: ReportFormat | null;
    },
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: SEND_SCREEN_ON_OFF_REPORT_EMAIL,
      variables: {
        input: {
          email,
          offScreenColumnFilters: filters?.offScreenColumnFilters?.length
            ? filters.offScreenColumnFilters
            : null,
          noScheduleColumnFilters: filters?.noScheduleColumnFilters?.length
            ? filters.noScheduleColumnFilters
            : null,
          // `?? null` so an explicit 0 survives, matching the list query.
          minOffDays: filters?.minOffDays ?? null,
          format: filters?.format || null,
        },
      },
    });
    return data.sendScreenOnOffReportEmail;
  },

  sendActiveScreenReportEmail: async (
    email: string,
    date?: string | null,
    format?: ReportFormat | null,
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: SEND_ACTIVE_SCREEN_REPORT_EMAIL,
      variables: {
        input: {
          email,
          date: date || null,
          format: format || null,
        },
      },
    });
    return data.sendActiveScreenReportEmail;
  },

  sendCreativeReportEmail: async (
    email: string,
    creativeId: string,
    filters?: {
      startDate?: string | null;
      endDate?: string | null;
      cityId?: string | null;
      regionId?: string | null;
      format?: ReportFormat | null;
    },
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: SEND_CREATIVE_REPORT_EMAIL,
      variables: {
        input: {
          email,
          creativeId,
          startDate: filters?.startDate || null,
          endDate: filters?.endDate || null,
          cityId: filters?.cityId || null,
          regionId: filters?.regionId || null,
          format: filters?.format || null,
        },
      },
    });
    return data.sendCreativeReportEmail;
  },

  downloadCreativeReportWithEdits: async (input: {
    creativeId: string;
    startDate?: string | null;
    endDate?: string | null;
    cityId?: string | null;
    regionId?: string | null;
    totalScreenTime?: number;
    numberOfTimesPlayed?: number;
    screenEdits?: Array<{
      screenName: string;
      duration?: number;
      numberOfTimesPlayed?: number;
    }>;
    format?: ReportFormat | null;
  }): Promise<{ base64: string; fileName: string; mimeType: string }> => {
    const { data } = await client.mutate({
      mutation: DOWNLOAD_CREATIVE_REPORT_WITH_EDITS,
      variables: { input },
    });
    return data.downloadCreativeReport;
  },
};
